import React from 'react';
import {
  useDiscussionPosts,
  useAddPostMutation,
  useDiscussionViewMutation,
  type MoodleInstance,
} from '../../../../hooks/useMoodleHooks';
import type { MoodleForumPost } from '../../../../types/moodleTypes';
import PostItem from './PostItem';
import AddPostForm from './forms/AddPostForm';
import { plus, minus } from '@harbor/elements/icons';
import { HbrIcon } from '@harbor/elements-react-standalone';

type Props = {
  discussionId: number;
  postId?: number;
  onBack?: () => void;
  instance?: MoodleInstance;
};

// Helper function to transform a flat list of posts into a nested tree.
function buildPostTree(posts: MoodleForumPost[]): MoodleForumPost[] {
  if (!posts || posts.length === 0) return [];

  const postMap = new Map<number, MoodleForumPost>();
  const rootPosts: MoodleForumPost[] = [];

  posts.forEach(post => {
    post.children = [];
    postMap.set(post.id, post);
  });

  posts.forEach(post => {
    if (post.parentid != null && postMap.has(post.parentid)) {
      postMap.get(post.parentid)?.children?.push(post);
    } else {
      rootPosts.push(post);
    }
  });

  return rootPosts;
}

export default function DiscussionDetails({ postId, discussionId, onBack, instance = 'external' }: Props) {
  const { data, isLoading, error } = useDiscussionPosts(
    discussionId,
    {
      sortby: 'created',
      sortdirection: 'ASC',
    },
    undefined,
    instance
  );

  const posts: MoodleForumPost[] = React.useMemo(() => {
    const raw: any = data;
    if (!raw) return [];
    const postList = Array.isArray(raw) ? raw : raw?.posts ?? [];
    return postList.sort((a, b) => a.timecreated - b.timecreated);
  }, [data]);

  const postTree = React.useMemo(() => buildPostTree(posts), [posts]);

  const rootPost = React.useMemo(
    () => posts.find((p) => p.parentid == null || p.hasparent === false) || posts[0] || null,
    [posts]
  );

  const [rootReplyOpen, setRootReplyOpen] = React.useState(false);
  const addPost = useAddPostMutation(discussionId, instance);

  const viewDiscussion = useDiscussionViewMutation(discussionId, instance);
  React.useEffect(() => {
    viewDiscussion.mutate();
    // eslint-disable-next-line
  }, [discussionId, instance]);

  if (isLoading) {
    return (
      <div className="center" aria-busy="true">
        <progress aria-label="Loading posts" />
        <style>{`.center{display:flex;align-items:center;justify-content:center;padding:var(--hbr-spacing-lg);}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="error">
        {(error as Error).message}
        <style>{`.error{color:red;font-family:var(--hbr-font-body);}`}</style>
      </p>
    );
  }

  const canReplyTop = !!rootPost;

  return (
    <div className="discussion-details">
      <div className="header-row">
        {onBack && (
          <button type="button" className="btn btn-outline" onClick={onBack} aria-label="Back to discussions">
            ← Back
          </button>
        )}

        <div className="header-actions">
          {canReplyTop && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setRootReplyOpen((v) => !v)}
              aria-pressed={rootReplyOpen}
              aria-label={rootReplyOpen ? 'Close reply form' : 'Reply to top post'}
              title={rootReplyOpen ? 'Close reply form' : 'Reply to top post'}
            >
              <HbrIcon asset={rootReplyOpen ? minus : plus} />
            </button>
          )}
        </div>
      </div>

      {rootReplyOpen && rootPost && (
        <div className="reply-composer">
          <AddPostForm
            onSubmit={(payload) =>
              addPost.mutate(
                { postid: rootPost.id, subject: payload.subject, message: payload.message, messageformat: 1 },
                { onSuccess: () => setRootReplyOpen(false) }
              )
            }
            submitting={addPost.isPending}
            onBack={() => setRootReplyOpen(false)}
          />
        </div>
      )}

      <ul className="post-list">
        {postTree.map((p) => (
          <PostItem key={p.id} discussionId={discussionId} post={p} instance={instance} />
        ))}
      </ul>

      <style>
        {`
          .discussion-details { display: block; }
          .header-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--hbr-spacing-sm);
            margin-bottom: var(--hbr-spacing-sm);
          }
          .header-actions {
            display: flex;
            gap: 8px;
            margin-left: auto;
          }
          .btn, .icon-btn {
            display:inline-flex; align-items:center; justify-content:center; gap: var(--hbr-spacing-2xs);
            padding:0.6rem 1rem; border-radius:10px; border:1px solid rgba(0,0,0,0.2);
            background:#fff; color:#0a66ff; cursor:pointer;
            font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); font-weight: var(--hbr-font-weight-semibold);
          }
          .btn:hover, .icon-btn:hover { background: rgba(10,102,255,0.08); }
          .btn-outline { background: transparent; color:#0a66ff; }
          .icon-btn {
            width: 32px; height: 32px; border-radius: 8px;
            border: 1px solid rgba(0,0,0,0.15);
            background: #fff; transition: background 0.2s, box-shadow 0.2s;
            padding:0; font-size:18px;
          }
          .icon-btn:hover { background: rgba(10,102,255,0.06); box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
          .reply-composer { margin-bottom: var(--hbr-spacing-md); }
          .post-list { list-style:none; margin:0; padding:0; }
          .post-list > li + li { margin-top: var(--hbr-spacing-sm); }
        `}
      </style>
    </div>
  );
}